import { millisecondsInDay } from '@alltrails/data-formatting';

const toTwoDigitString = (x: number) => String(x).padStart(2, '0');

/**
 * Calculates the number of days between two UTC dates, not impacted by time of day.
 * Returns negative if the start date is after the end date.
 * @param start Start date
 * @param end End date
 * @returns Number of days
 */
const daysBetweenUTC = (start: Date, end: Date) => {
  const startUTC = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endUTC = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

  return Math.floor((endUTC - startUTC) / millisecondsInDay);
};

/**
 * Creates a date/time string for fetching map overlay tiles.
 * The string is in the format today[+/-][dayOffset]DT[HH]_[mm]_[ss]Z
 * @param targetUTC UTC date
 * @returns date/time string
 */
export const getOverlayTilesDateTimeString = (targetUTC: number | string) => {
  const targetDate = new Date(targetUTC);
  const dayOffset = daysBetweenUTC(new Date(), targetDate);
  const dayString = `today${dayOffset < 0 ? '-' : '+'}${Math.abs(dayOffset)}D`;
  const timeString = `T${toTwoDigitString(targetDate.getUTCHours())}_${toTwoDigitString(targetDate.getUTCMinutes())}_${toTwoDigitString(targetDate.getUTCSeconds())}Z`;

  return `${dayString}${timeString}`;
};

/**
 * Creates a Date object such that date.getUTC____ returns localized time, based on the provided offset
 * @param utc UTC date
 * @param utcOffsetSeconds UTC timezone offset, positive values indicate timezones ahead of UTC
 * @returns Date
 */
export const getLocalizedUTCDate = (utc: number | string, utcOffsetSeconds: number) => {
  const utcMs = new Date(utc).getTime();
  return new Date(utcMs + utcOffsetSeconds * 1000);
};

/**
 * Reverses the conversion made by getLocalizedUTCDate
 * @param localizedUTC Localized UTC date
 * @param utcOffsetSeconds UTC timezone offset, positive values indicate timezones ahead of UTC
 * @returns UTC in ms
 */
export const getUTCFromLocalized = (localizedUTC: number | string, utcOffsetSeconds: number) => {
  const utcMs = new Date(localizedUTC).getTime();
  return utcMs - utcOffsetSeconds * 1000;
};

/**
 * Creates a date string in the format YYYY-MM-DD for the given UTC offset
 * @param utc UTC date
 * @param utcOffsetSeconds UTC timezone offset, positive values indicate timezones ahead of UTC
 * @returns date string
 */
export const getLocalizedDateString = (utc: number | string, utcOffsetSeconds: number) => {
  const localDate = getLocalizedUTCDate(utc, utcOffsetSeconds);

  return [localDate.getUTCFullYear(), toTwoDigitString(localDate.getUTCMonth() + 1), toTwoDigitString(localDate.getUTCDate())].join('-');
};

/**
 * Returns the 12:00am time, in ms, for the given UTC date
 * @param utc UTC date
 * @returns 12:00am in ms
 */
export const getUTCDateStart = (utc: number | string) => {
  const utcMs = new Date(utc).getTime();
  return utcMs - (utcMs % millisecondsInDay);
};

/**
 * Returns the localized 12:00am time, in ms, for the given UTC date
 * @param utc UTC date
 * @param utcOffsetSeconds UTC timezone offset, positive values indicate timezones ahead of UTC
 * @returns 12:00am, local, in ms
 */
export const getLocalizedDateStart = (utc: number | string, utcOffsetSeconds: number) => {
  return getUTCDateStart(getLocalizedUTCDate(utc, utcOffsetSeconds).getTime());
};
